import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import './NewDrop.css';

const NewDrop = () => {
  const [newCollectionProducts, setNewCollectionProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const cardsToShow = isMobile ? 1 : 3; // Show 1 card on mobile, 3 on desktop

  // Touch handling for swipe functionality
  const touchStartX = useRef(null);
  const touchEndX = useRef(null);
  const touchStartY = useRef(null);
  const touchEndY = useRef(null);

  // Detect mobile view
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const fetchNewCollectionProducts = async () => {
      try {
        setLoading(true);
        
        // Fetch only products marked as new collection/drop
        const productsRef = collection(db, 'products');
        const q = query(
          productsRef, 
          where('isNewCollection', '==', true)
        );
        const querySnapshot = await getDocs(q);
        
        const products = [];
        querySnapshot.forEach((doc) => {
          products.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        // Sort products by priority first (High > Medium > Low), then by creation date (newest first)
        const priorityOrder = { 'High': 1, 'Medium': 2, 'Low': 3 };
        const sortedProducts = products.sort((a, b) => {
          const aPriority = a.newDropPriority || '';
          const bPriority = b.newDropPriority || '';
          
          // Get priority order (4 = no priority, goes last)
          const aPriorityOrder = priorityOrder[aPriority] || 4;
          const bPriorityOrder = priorityOrder[bPriority] || 4;
          
          // First sort by priority
          if (aPriorityOrder !== bPriorityOrder) {
            return aPriorityOrder - bPriorityOrder; // Lower number = higher priority
          }
          
          // If same priority, sort by creation date (newest first)
          const aDate = new Date(a.createdAt?.toDate?.() || a.createdAt || 0);
          const bDate = new Date(b.createdAt?.toDate?.() || b.createdAt || 0);
          return bDate - aDate; // Newest first
        });
        
        setNewCollectionProducts(sortedProducts);
      } catch (error) {
        console.error('Error fetching new collection products:', error);
        // If index error or no new collection products, show empty state
        setNewCollectionProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchNewCollectionProducts();
  }, []);

  // Auto-play carousel - scroll one card at a time
  useEffect(() => {
    if (!isAutoPlaying || loading || newCollectionProducts.length === 0) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentIndex((prev) => {
        const maxIndex = newCollectionProducts.length - cardsToShow;
        return prev >= maxIndex ? 0 : prev + 1;
      });
    }, 4000); // Change every 4 seconds

    return () => clearInterval(interval);
  }, [isAutoPlaying, loading, newCollectionProducts.length, cardsToShow]);

  const goToPrevious = () => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => {
      const maxIndex = newCollectionProducts.length - cardsToShow;
      return Math.min(maxIndex, prev + 1);
    });
    setIsAutoPlaying(false);
    setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  // Touch event handlers for swipe functionality
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
    touchEndY.current = e.touches[0].clientY;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current || !touchStartY.current || !touchEndY.current) {
      return;
    }

    const deltaX = touchStartX.current - touchEndX.current;
    const deltaY = touchStartY.current - touchEndY.current;

    // Check if it's a horizontal swipe (not vertical scroll)
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      const isLeftSwipe = deltaX > 50;
      const isRightSwipe = deltaX < -50;

      if (isLeftSwipe) {
        // Swipe left - go to next
        goToNext();
      } else if (isRightSwipe) {
        // Swipe right - go to previous
        goToPrevious();
      }
    }

    // Reset touch values
    touchStartX.current = null;
    touchEndX.current = null;
    touchStartY.current = null;
    touchEndY.current = null;
  };

  const formatPrice = (price) => {
    return `₹${price?.toLocaleString() || 0}`;
  };

  const getProductImage = (product) => {
    // Match Collections.js image loading logic
    if (!product.imageURLs || product.imageURLs.length === 0) {
      return '/images/fallbacks/formal-wear.webp';
    }

    const firstImage = product.imageURLs[0];
    
    // Check if optimized images exist
    if (firstImage.optimized) {
      // Use 768w for good balance
      return firstImage.optimized['768w']?.url || 
             firstImage.optimized['1080w']?.url || 
             firstImage.original?.url || 
             '/images/fallbacks/formal-wear.webp';
    }

    // Fallback to original image
    return firstImage.original?.url || '/images/fallbacks/formal-wear.webp';
  };

  return (
    <section className="newdrop-section">
      <div className="newdrop-container">
        {/* Section Header */}
        <div className="newdrop-header">
          <h2 className="newdrop-title">The New Drop</h2>
          <p className="newdrop-subtitle">
            Fresh arrivals, crafted with contemporary elegance for the modern gentleman.
          </p>
        </div>

        {/* Products Carousel */}
        {loading ? (
          <div className="newdrop-carousel-wrapper">
            <div className="newdrop-carousel-track">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="newdrop-card newdrop-card-loading">
                  <div className="newdrop-img-wrapper">
                    <div className="newdrop-skeleton"></div>
                  </div>
                  <div className="newdrop-info">
                    <div className="newdrop-skeleton-text newdrop-skeleton-title"></div>
                    <div className="newdrop-skeleton-text newdrop-skeleton-price"></div>
                    <div className="newdrop-skeleton-text newdrop-skeleton-btn"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : newCollectionProducts.length > 0 ? (
          <div className="newdrop-carousel-container">
            {/* Navigation Arrows */}
            <button 
              className="newdrop-carousel-arrow newdrop-carousel-arrow-left"
              onClick={goToPrevious}
              disabled={currentIndex === 0}
              aria-label="Previous products"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M15 18L9 12L15 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            <div 
              className="newdrop-carousel-wrapper"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
            >
              <div 
                className="newdrop-carousel-track"
                style={{ 
                  transform: isMobile 
                    ? `translateX(-${currentIndex * 83}%)` // 83% = card width on mobile
                    : `translateX(-${currentIndex * (100 / cardsToShow)}%)`,
                  transition: 'transform 0.5s ease'
                }}
              >
                {newCollectionProducts.map((product, index) => (
                  <Link 
                    key={product.id} 
                    to={`/product/${product.id}`}
                    className="newdrop-card"
                  >
                    <div className="newdrop-img-wrapper">
                      <img
                        src={getProductImage(product)}
                        alt={product.name || 'New Drop Piece'}
                        className="newdrop-img"
                        loading={index < 4 ? "eager" : "lazy"}
                      />
                    </div>
                    <div className="newdrop-info">
                      <h3 className="newdrop-product-name">{product.name || 'Untitled'}</h3>
                      <p className="newdrop-price">{formatPrice(product.basePrice)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            <button 
              className="newdrop-carousel-arrow newdrop-carousel-arrow-right"
              onClick={goToNext}
              disabled={currentIndex >= newCollectionProducts.length - cardsToShow}
              aria-label="Next products"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 18L15 12L9 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        ) : (
          // No Products Available
          <div className="newdrop-empty-state">
            <div className="newdrop-empty-icon">
              <svg width="60" height="60" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" fill="#D6A464"/>
              </svg>
            </div>
            <h3 className="newdrop-empty-title">New Arrivals Coming Soon</h3>
            <p className="newdrop-empty-text">Our latest collection is being crafted with precision</p>
            <Link to="/collections" className="newdrop-empty-cta">
              Browse Collections
            </Link>
          </div>
        )}

        {/* View All Link */}
        {!loading && newCollectionProducts.length > 0 && (
          <div className="newdrop-footer">
            <Link to="/collections" className="newdrop-viewall-link">
              View All Collections
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12H19M12 5L19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
};

export default NewDrop;
